import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  useTheme, 
  TextField, 
  InputAdornment,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Menu,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import { getToken, base_url } from '../../config/config';
import axios from 'axios';
import { 
  Search,
  AddCircleOutlineOutlined,
  LocalShippingOutlined,
  FilterListOutlined,
  MoreVert,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  WarningAmberOutlined,
  InfoOutlined,
  VisibilityOutlined,
  LabelOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const StockMovementsList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stockMoves, setStockMoves] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null });
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    state: 'all',
    location: 'all',
    anr: ''
  });
  const [anrSearchText, setAnrSearchText] = useState('');
  const [anrList, setAnrList] = useState([]);
  const [anrFilterOpen, setAnrFilterOpen] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    totalMovements: 0,
    pendingMovements: 0,
    doneMovements: 0,
    incomingMovements: 0,
    outgoingMovements: 0
  });

  useEffect(() => {
    fetchStockMovements();
    fetchAnrList();
  }, []);

  const fetchStockMovements = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${base_url}/v1/stock/moves`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const movementsData = response.data.data.map(move => ({
          ...move,
          id: move._id,
          productName: move.productId?.name || 'Unknown Product',
          artikelNummer: move.productId?.artikelNummer || '',
          materialType: move.productId?.materialType?.name || '',
          unitName: move.productId?.unitName || '',
          formattedDate: format(new Date(move.moveDate), 'dd.MM.yyyy HH:mm')
        }));
        
        setStockMoves(movementsData);
        calculateStats(movementsData);
      }
    } catch (error) {
      console.error('Error fetching stock movements:', error);
      setAlert({
        open: true,
        message: 'Failed to load stock movements',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnrList = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${base_url}/v1/anrlist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.data) {
        setAnrList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ANR list:', error);
    }
  };

  const calculateStats = (movements) => {
    const stats = {
      totalMovements: movements.length,
      pendingMovements: movements.filter(move => ['draft', 'assigned'].includes(move.state)).length,
      doneMovements: movements.filter(move => move.state === 'done').length,
      incomingMovements: movements.filter(move => move.toLocation === 'stock' && move.state === 'done').length,
      outgoingMovements: movements.filter(move => move.fromLocation === 'stock' && move.state === 'done').length
    };
    
    setStats(stats);
  };

  const handleOpenActionMenu = (event, movement) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedMovement(movement);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
  };

  const handleOpenFilterMenu = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    setFilterMenuAnchor(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };

  const handleActionSelect = (action) => {
    handleCloseActionMenu();
    
    if (!selectedMovement) return;
    
    switch (action) {
      case 'edit':
        navigate(`/stock/movements/edit/${selectedMovement.id}`);
        break;
      case 'view':
        navigate(`/stock/movements/${selectedMovement.id}`);
        break;
      case 'complete':
        setConfirmDialog({ open: true, type: 'complete' });
        break;
      case 'cancel':
        setConfirmDialog({ open: true, type: 'cancel' });
        break;
      case 'delete':
        setConfirmDialog({ open: true, type: 'delete' });
        break;
      default:
        break;
    }
  };

  const handleConfirmDialogAction = async () => {
    try {
      const token = getToken();
      
      if (confirmDialog.type === 'delete') {
        await axios.delete(`${base_url}/v1/stock/moves/${selectedMovement.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        setStockMoves(stockMoves.filter(move => move.id !== selectedMovement.id));
        setAlert({
          open: true,
          message: 'Stock movement deleted successfully',
          severity: 'success'
        });
      } else if (['complete', 'cancel'].includes(confirmDialog.type)) {
        const newState = confirmDialog.type === 'complete' ? 'done' : 'cancelled';
        
        const response = await axios.put(
          `${base_url}/v1/stock/moves/${selectedMovement.id}`,
          { state: newState },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          // Update the movement in the local state
          const updatedMoves = stockMoves.map(move => 
            move.id === selectedMovement.id 
              ? { ...move, state: newState } 
              : move
          );
          setStockMoves(updatedMoves);
          calculateStats(updatedMoves);
          
          setAlert({
            open: true,
            message: `Stock movement ${newState === 'done' ? 'completed' : 'cancelled'} successfully`,
            severity: 'success'
          });
        }
      }
    } catch (error) {
      console.error('Error handling confirmation action:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Operation failed',
        severity: 'error'
      });
    } finally {
      setConfirmDialog({ open: false, type: null });
    }
  };

  const applyFilters = (movements) => {
    return movements.filter(move => {
      // Filter by status
      if (filters.state !== 'all' && move.state !== filters.state) {
        return false;
      }
      
      // Filter by location
      if (filters.location === 'incoming' && move.toLocation !== 'stock') {
        return false;
      }
      if (filters.location === 'outgoing' && move.fromLocation !== 'stock') {
        return false;
      }
      if (filters.location === 'internal' && 
          (move.fromLocation === 'stock' || move.toLocation === 'stock')) {
        return false;
      }
      
      // Filter by ANR
      if (filters.anr && move.anrId !== filters.anr) {
        return false;
      }
      
      // Filter by search text
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        return (
          move.moveId.toLowerCase().includes(searchLower) ||
          move.productName.toLowerCase().includes(searchLower) ||
          move.artikelNummer.toLowerCase().includes(searchLower) ||
          (move.anrId && move.anrId.toLowerCase().includes(searchLower))
        );
      }
      
      return true;
    });
  };

  const getStatusChip = (status) => {
    let color, icon, label;
    
    switch (status) {
      case 'draft':
        color = colors.grey[500];
        icon = <InfoOutlined />;
        label = 'Draft';
        break;
      case 'assigned':
        color = colors.blueAccent[500];
        icon = <LabelOutlined />;
        label = 'Assigned';
        break;
      case 'done':
        color = colors.greenAccent[500];
        icon = <CheckCircleOutlined />;
        label = 'Done';
        break;
      case 'cancelled':
        color = colors.redAccent[500];
        icon = <CancelOutlined />;
        label = 'Cancelled';
        break;
      default:
        color = colors.grey[500];
        icon = <InfoOutlined />;
        label = status;
    }
    
    return (
      <Chip 
        icon={icon}
        label={label}
        size="small"
        sx={{ 
          bgcolor: alpha(color, 0.2),
          color: color,
          fontWeight: status === 'draft' ? 'normal' : 'bold',
          '& .MuiChip-icon': {
            color: 'inherit'
          }
        }}
      />
    );
  };

  const getMovementTypeChip = (fromLocation, toLocation) => {
    let color, label;
    
    if (toLocation === 'stock') {
      color = 'success';
      label = 'Stock Receipt';
    } else if (fromLocation === 'stock') {
      color = 'error';
      label = 'Stock Issue';
    } else {
      color = 'info';
      label = 'Transfer';
    }
    
    return (
      <Chip 
        label={label}
        color={color}
        size="small"
        variant="outlined"
      />
    );
  };

  const columns = [
    { 
      field: 'moveId', 
      headerName: 'Move ID', 
      flex: 1 
    },
    { 
      field: 'productName', 
      headerName: 'Product', 
      flex: 1.5,
      renderCell: (params) => (
        <div>
          <Typography>{params.value}</Typography>
          <Typography variant="caption" color={colors.grey[400]}>
            {params.row.artikelNummer}
          </Typography>
        </div>
      )
    },
    { 
      field: 'quantity', 
      headerName: 'Quantity', 
      width: 110,
      renderCell: (params) => (
        <Typography>
          {params.value} {params.row.unitName}
        </Typography>
      )
    },
    { 
      field: 'movementType', 
      headerName: 'Type', 
      width: 130,
      renderCell: (params) => getMovementTypeChip(params.row.fromLocation, params.row.toLocation)
    },
    { 
      field: 'anrId', 
      headerName: 'ANR', 
      width: 120,
      renderCell: (params) => params.value ? (
        <Chip 
          label={params.value}
          size="small"
          color="primary"
          onClick={() => navigate(`/stock/anr/${params.value}`)}
          sx={{ cursor: 'pointer' }}
        />
      ) : null
    },
    { 
      field: 'state', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => getStatusChip(params.value)
    },
    { 
      field: 'formattedDate', 
      headerName: 'Date', 
      width: 160
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 80,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleOpenActionMenu(e, params.row)}>
          <MoreVert />
        </IconButton>
      )
    }
  ];

  // Get alpha color with transparency
  const alpha = (color, opacity) => {
    return theme.palette.mode === 'dark'
      ? `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, ${opacity})`
      : color;
  };

  const filteredStockMoves = applyFilters(stockMoves);

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h2" color={colors.grey[100]} fontWeight="bold" mb="5px">
            Stock Movements
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            Manage inventory movements and transfers
          </Typography>
        </Box>
        
        <Box>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<FilterListOutlined />}
            onClick={handleOpenFilterMenu}
            sx={{ mr: 1 }}
          >
            Filter
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddCircleOutlineOutlined />}
            onClick={() => navigate('/stock/movements/new')}
          >
            Create Movement
          </Button>
        </Box>
      </Box>
      
      {/* STATS CARDS */}
      <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.primary[400], height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {stats.totalMovements}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                Total Movements
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.primary[400], height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold" color={colors.blueAccent[400]}>
                {stats.pendingMovements}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                Pending Movements
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.primary[400], height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold" color={colors.greenAccent[400]}>
                {stats.doneMovements}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                Completed Movements
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.primary[400], height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold" color={colors.greenAccent[400]}>
                {stats.incomingMovements}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                Incoming to Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: colors.primary[400], height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold" color={colors.redAccent[400]}>
                {stats.outgoingMovements}
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                Outgoing from Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SEARCH BAR */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={2}
      >
        <TextField
          variant="filled"
          label="Search Stock Movements"
          placeholder="Search by ID, Product, or ANR"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ width: '70%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        
        <Box display="flex" alignItems="center">
          {filters.state !== 'all' && (
            <Chip 
              label={`Status: ${filters.state.charAt(0).toUpperCase() + filters.state.slice(1)}`}
              onDelete={() => handleFilterChange('state', 'all')}
              sx={{ mr: 1 }}
            />
          )}
          
          {filters.location !== 'all' && (
            <Chip 
              label={`Type: ${filters.location.charAt(0).toUpperCase() + filters.location.slice(1)}`}
              onDelete={() => handleFilterChange('location', 'all')}
              sx={{ mr: 1 }}
            />
          )}
          
          {filters.anr && (
            <Chip 
              label={`ANR: ${filters.anr}`}
              onDelete={() => handleFilterChange('anr', '')}
              sx={{ mr: 1 }}
            />
          )}
          
          {(filters.state !== 'all' || filters.location !== 'all' || filters.anr) && (
            <Button 
              variant="text" 
              size="small"
              onClick={() => setFilters({ state: 'all', location: 'all', anr: '' })}
            >
              Clear All
            </Button>
          )}
        </Box>
      </Box>
      
      {/* ACTIVE FILTERS SUMMARY */}
      {(filters.state !== 'all' || filters.location !== 'all' || filters.anr) && (
        <Typography variant="body2" color={colors.grey[300]} mb={2}>
          Showing {filteredStockMoves.length} of {stockMoves.length} movements
        </Typography>
      )}

      {/* DATA GRID */}
      <Box
        height="calc(100vh - 380px)"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredStockMoves}
            columns={columns}
            components={{ Toolbar: GridToolbar }}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </Box>

      {/* FILTER MENU */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={handleCloseFilterMenu}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 300,
            padding: '8px'
          },
        }}
      >
        <Typography variant="h6" sx={{ px: 2, py: 1 }}>Filter Movements</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ px: 2, mb: 2 }}>
          <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="done">Done</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Movement Type</InputLabel>
            <Select
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              label="Movement Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="incoming">Incoming to Stock</MenuItem>
              <MenuItem value="outgoing">Outgoing from Stock</MenuItem>
              <MenuItem value="internal">Internal Transfers</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            fullWidth 
            variant="outlined" 
            color="primary" 
            onClick={() => setAnrFilterOpen(true)}
            sx={{ mb: 1 }}
          >
            {filters.anr ? `ANR: ${filters.anr}` : 'Select ANR'}
          </Button>
          
          {filters.anr && (
            <Button 
              fullWidth 
              variant="text" 
              color="error" 
              onClick={() => handleFilterChange('anr', '')}
              size="small"
            >
              Clear ANR Filter
            </Button>
          )}
        </Box>
        
        <Divider sx={{ mb: 1 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1 }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={() => {
              setFilters({ state: 'all', location: 'all', anr: '' });
              handleCloseFilterMenu();
            }}
            sx={{ mr: 1 }}
          >
            Reset
          </Button>
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={handleCloseFilterMenu}
          >
            Apply
          </Button>
        </Box>
      </Menu>

      {/* ACTION MENU */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        <MenuItem onClick={() => handleActionSelect('view')}>
          <VisibilityOutlined sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => handleActionSelect('edit')}>
          <EditOutlined sx={{ mr: 1 }} /> Edit
        </MenuItem>
        {selectedMovement && ['draft', 'assigned'].includes(selectedMovement.state) && (
          <MenuItem onClick={() => handleActionSelect('complete')}>
            <CheckCircleOutlined sx={{ mr: 1 }} /> Mark as Done
          </MenuItem>
        )}
        {selectedMovement && ['draft', 'assigned'].includes(selectedMovement.state) && (
          <MenuItem onClick={() => handleActionSelect('cancel')}>
            <CancelOutlined sx={{ mr: 1 }} /> Cancel Movement
          </MenuItem>
        )}
        {selectedMovement && selectedMovement.state !== 'done' && (
          <MenuItem onClick={() => handleActionSelect('delete')} sx={{ color: colors.redAccent[500] }}>
            <DeleteOutlined sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* ANR SELECTION DIALOG */}
      <Dialog 
        open={anrFilterOpen}
        onClose={() => setAnrFilterOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Project ANR</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Search ANR"
            fullWidth
            variant="outlined"
            value={anrSearchText}
            onChange={(e) => setAnrSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {anrList
              .filter(anr => anr.ANR.toLowerCase().includes(anrSearchText.toLowerCase()))
              .map(anr => (
                <Card 
                  key={anr.ANR} 
                  sx={{ 
                    mb: 1, 
                    cursor: 'pointer',
                    border: filters.anr === anr.ANR ? `2px solid ${colors.greenAccent[500]}` : 'none',
                    '&:hover': {
                      bgcolor: colors.primary[500]
                    }
                  }}
                  onClick={() => {
                    handleFilterChange('anr', anr.ANR);
                    setAnrFilterOpen(false);
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h5">{anr.ANR}</Typography>
                    <Typography variant="body2" color={colors.grey[300]}>
                      {anr.Auftragsbezeichnung || 'No description'}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              handleFilterChange('anr', '');
              setAnrFilterOpen(false);
            }}
          >
            Clear Selection
          </Button>
          <Button onClick={() => setAnrFilterOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRM DIALOG */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: null })}>
        <DialogTitle>
          {confirmDialog.type === 'delete' ? 'Delete Movement' : 
           confirmDialog.type === 'complete' ? 'Complete Movement' : 
           'Cancel Movement'}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.type === 'delete' && (
            <Typography>
              Are you sure you want to delete this stock movement? This action cannot be undone.
            </Typography>
          )}
          {confirmDialog.type === 'complete' && (
            <>
              <Typography paragraph>
                Are you sure you want to mark this movement as done? This will:
              </Typography>
              <ul>
                <li>
                  <Typography>
                    {selectedMovement?.toLocation === 'stock' 
                      ? 'Increase the stock level of the product' 
                      : selectedMovement?.fromLocation === 'stock'
                        ? 'Decrease the stock level of the product'
                        : 'Not affect stock levels (internal transfer)'}
                  </Typography>
                </li>
                {selectedMovement?.anrId && (
                  <li>
                    <Typography>
                      Update material costs for project ANR: {selectedMovement.anrId}
                    </Typography>
                  </li>
                )}
              </ul>
            </>
          )}
          {confirmDialog.type === 'cancel' && (
            <Typography>
              Are you sure you want to cancel this stock movement? It will be marked as cancelled and won't affect stock levels.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: null })}>
            No
          </Button>
          <Button 
            onClick={handleConfirmDialogAction} 
            color={confirmDialog.type === 'delete' ? 'error' : 'primary'}
            variant="contained"
          >
            Yes, {confirmDialog.type === 'delete' ? 'Delete' : 
                  confirmDialog.type === 'complete' ? 'Complete' : 
                  'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ALERT SNACKBAR */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StockMovementsList;